import { Box, Button, Chip, Link, Typography } from '@mui/material';
import { Filters } from './components';
import { FiltersCard } from './components/Filters/FiltersCard.tsx';
import { PreviewContext } from './contexts';
import { useUsersPreview } from './hooks';
import {
  Edit,
  KeyboardArrowDown,
  Lock,
  Star,
  ArrowBack,
  EditOff,
} from '@mui/icons-material';
import { GroupsTable } from './components/GroupByTabel';
import { UsersTable } from './UsersTable.tsx';
import { theme } from '@store';
import { t } from 'i18next';
import { ConfirmationModal, Dropdown, ReportHistoryModal } from '@components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { TruncatedTextWithConditionalTooltip } from '@components';

export const UsersPreview = () => {
  const {
    handleBackToEdit,
    handleGoToReports,
    contextValues,
    columns,
    report,
    handleSaveReport,
    hideSaveOptions,
    dropdownMenu,
    previewMode,
    isDeleteModalOpen,
    handleCloseDeleteModal,
    isDeleteLoading,
    handleDeleteReport,
    handleDeleteReportFavorite,
    makeReportFavoriteIsPending,
    handleToggleReportPrivate,
    privateIsPending,
    isReportHistoryModalOpen,
    handleCloseHistoryModal,
    isLoading,
  } = useUsersPreview();

  return (
    <PreviewContext.Provider value={contextValues}>
      {/* Header section */}
      <Box
        sx={{
          [theme.breakpoints.down('md')]: {
            display: 'none',
          },
        }}
      >
        <Link
          underline='always'
          onClick={handleGoToReports}
          sx={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            gap: '8px',
            mb: '1rem',
            cursor: 'pointer',
          }}
        >
          <ArrowBack sx={{ fontSize: '1.3rem' }} color='primary' />
          <Typography sx={{ color: 'primary' }}>
            Back to previous page
          </Typography>
        </Link>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: `calc(100% - ${previewMode ? 350 : 170}px)`,
            [theme.breakpoints.up('md')]: {
              width: `calc(100% - ${previewMode ? 320 : 130}px)`,
            },
            display: 'flex',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Link
            underline='always'
            onClick={handleGoToReports}
            sx={{
              cursor: 'pointer',
              mt: 0.875,
              [theme.breakpoints.up('md')]: {
                display: 'none',
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: '2rem' }} color='primary' />
          </Link>
          <TruncatedTextWithConditionalTooltip
            variant='h4'
            text={report?.name ?? 'User'}
            width='90%'
            sx={{ mb: 2, mt: 2, display: isLoading ? 'none' : 'inherit' }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {previewMode ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                sx={{ fontWeight: 'regular', fontSize: '1rem' }}
                color={'primary'}
                variant={'outlined'}
                onClick={handleBackToEdit}
              >
                {t('reporting.report.preview.continueToEdit')}
              </Button>
              {!hideSaveOptions && (
                <Button
                  variant='contained'
                  color='secondary'
                  sx={{
                    color: theme.palette.common.white,
                    fontSize: '1rem',
                  }}
                  onClick={handleSaveReport}
                >
                  {t('reporting.report.preview.saveReport')}
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex' }}>
              <Button
                startIcon={<Edit />}
                sx={{ fontWeight: 'regular', fontSize: '1rem' }}
                color={'primary'}
                variant={'outlined'}
                disabled={hideSaveOptions}
                onClick={handleBackToEdit}
              >
                {t('general.edit')}
              </Button>
              <Dropdown
                menuList={dropdownMenu}
                disabled={!report?.id}
                buttonComponent={
                  <Button
                    sx={{ height: 40 }}
                    color='primary'
                    variant='outlined'
                  >
                    <KeyboardArrowDown />
                  </Button>
                }
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: isLoading ? 'none' : 'flex',
          gap: 1,
          alignItems: 'center',
          my: 1,
        }}
      >
        {report?.report_folder?.organization?.name && (
          <>
            <Typography variant='body1' sx={{ color: 'text.secondary' }}>
              {report?.report_folder?.organization?.name}
            </Typography>
            <Box
              sx={{
                height: '4px',
                width: '4px',
                borderRadius: '50%',
                backgroundColor: theme => theme.palette.grey[700],
              }}
            />
          </>
        )}

        {report?.report_folder?.name && (
          <>
            <Typography variant='body1' sx={{ color: 'text.secondary' }}>
              {report?.report_folder?.name}
            </Typography>
            <Box
              sx={{
                height: '4px',
                width: '4px',
                borderRadius: '50%',
                backgroundColor: theme => theme.palette.grey[700],
              }}
            />
          </>
        )}

        <Typography variant='body1' sx={{ color: 'text.secondary' }}>
          Users
        </Typography>
        {report?.favorite && (
          <Chip
            sx={{
              height: 24,
              ml: 1,
              '& .MuiChip-icon': {
                width: 16,
                height: 16,
              },
              '& .MuiChip-deleteIcon': {
                width: 16,
                height: 16,
              },
            }}
            icon={<Star />}
            label='Favorite'
            color='info'
            onDelete={handleDeleteReportFavorite}
            disabled={makeReportFavoriteIsPending}
          />
        )}
        {report?.private && (
          <Chip
            sx={{
              height: 24,
              ml: 1,
              '& .MuiChip-icon': {
                width: 16,
                height: 16,
              },
              '& .MuiChip-deleteIcon': {
                width: 16,
                height: 16,
              },
            }}
            icon={<Lock />}
            label='Private'
            onDelete={() => handleToggleReportPrivate(false)}
            disabled={privateIsPending || report.report_folder?.private}
          />
        )}
        <Chip
          sx={{
            height: 24,
            ml: 1,
            '& .MuiChip-icon': {
              width: 16,
              height: 16,
            },
            '& .MuiChip-deleteIcon': {
              width: 16,
              height: 16,
            },
          }}
          icon={hideSaveOptions ? <EditOff /> : <Edit />}
          label={`You ${hideSaveOptions ? "can't" : 'can'} edit this report`}
        />
      </Box>

      {report?.description && !isLoading && (
        <Typography
          variant='body2'
          sx={{
            pb: '1rem',
          }}
        >
          {report?.description}
        </Typography>
      )}

      {/* Filters and Tabs section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          flexDirection: 'row',
          gap: '1rem',
          width: '100%',
        }}
      >
        <FiltersCard />
        <Box
          sx={{
            overflowX: 'auto',
            width: '100%',
          }}
        >
          <Filters />
          {contextValues.filters.group_by ? (
            <GroupsTable columns={columns} />
          ) : (
            <UsersTable isLoading={isLoading} />
          )}
        </Box>
      </Box>
      <ConfirmationModal
        title={t('reporting.report.deleteModal.title')}
        message={t('reporting.report.deleteModal.message')}
        onConfirm={handleDeleteReport}
        onClose={handleCloseDeleteModal}
        isOpen={isDeleteModalOpen}
        isLoading={isDeleteLoading}
        confirmText={t('general.delete')}
      />
      <ReportHistoryModal
        isOpen={isReportHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        report_id={report?.id || ''}
      />
    </PreviewContext.Provider>
  );
};
